import React from 'react';
import { Calendar, Plane, Home, ChevronDown } from 'lucide-react';

interface TravelDatesProps {
  departureDate: string;
  returnDate: string;
  onDepartureDateChange: (date: string) => void;
  onReturnDateChange: (date: string) => void;
}

export function TravelDates({
  departureDate,
  returnDate,
  onDepartureDateChange,
  onReturnDateChange
}: TravelDatesProps) {
  const [isExpanded, setIsExpanded] = React.useState(!departureDate && !returnDate);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const getDuration = () => {
    if (!departureDate || !returnDate) return null;
    const start = new Date(departureDate);
    const end = new Date(returnDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const duration = getDuration();
  const hasDates = departureDate || returnDate;

  return (
    <div className="bg-white dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-700/40 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
      >
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600 dark:text-teal-400" />
          </div>
          <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
            Dates du voyage
          </span>

          {!isExpanded && hasDates && (
            <div className="hidden sm:flex items-center gap-2 ml-auto mr-2 text-xs text-gray-500 dark:text-gray-400">
              {departureDate && <span>{formatDateShort(departureDate)}</span>}
              {departureDate && returnDate && <span>-</span>}
              {returnDate && <span>{formatDateShort(returnDate)}</span>}
              {duration && (
                <span className="text-teal-600 dark:text-teal-400 font-medium">({duration}j)</span>
              )}
            </div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isExpanded ? contentRef.current?.scrollHeight : 0,
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div className="px-3 pb-3 sm:px-4 sm:pb-4">
          {/* Timeline layout */}
          <div className="flex items-stretch gap-2 sm:gap-3">
            {/* Departure */}
            <div className="flex-1 min-w-0">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Plane className="w-3 h-3 text-teal-500" />
                Depart
              </label>
              <input
                type="date"
                value={departureDate}
                onChange={(e) => onDepartureDateChange(e.target.value)}
                className="w-full px-2.5 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600/50 rounded-lg focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 dark:text-white transition-all text-[13px] sm:text-sm"
              />
            </div>

            {/* Duration pill */}
            {duration && (
              <div className="flex flex-col items-center justify-end pb-2">
                <div className="px-2 py-1 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-[11px] sm:text-xs font-semibold whitespace-nowrap">
                  {duration}j
                </div>
              </div>
            )}

            {/* Return */}
            <div className="flex-1 min-w-0">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Home className="w-3 h-3 text-amber-500" />
                Retour
              </label>
              <input
                type="date"
                value={returnDate}
                min={departureDate}
                onChange={(e) => onReturnDateChange(e.target.value)}
                className="w-full px-2.5 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600/50 rounded-lg focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 dark:text-white transition-all text-[13px] sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
